"use client";

// because "new Date()" causes error otherwise
import { c as _c } from "react/compiler-runtime";
import { TStaticContent } from "@/types/types";
import { cn } from "@/utils/helpers";
import { formatDistanceToNowStrict } from "date-fns";
import { Star } from "lucide-react";
import Image from "next/image";
import { ReactNode } from "react";
import RatingStars from "../fancy/rating-stars";
import { ReviewBody, ReviewContainer, ReviewFooter, ReviewHeader, ReviewSubtitle, ReviewTitle } from "../fancy/review";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { formatUserFullName } from "@/utils/formatters";
type ReviewCardProps = {
  review: TStaticContent["mockReviews"][number];
  showRating?: boolean;
  truncate?: boolean;
  className?: string;
  children?: ReactNode;
};

// INFO: not marking this component as "use client" will cause error because of "new Date()", extracting "new Date()" to a separate component and marking that with "use cache" will also not help because this component can be imported by a client component and "use cache" cannot be called in a component whose parent is a client component, so i have to mark it as a "use client" component (maybe "new Date()" cannot be used outside client components?)

export function ReviewCard(t0) {
  const $ = _c(40);
  const {
    review,
    showRating: t1,
    truncate: t2,
    className,
    children
  } = t0;
  const showRating = t1 === undefined ? true : t1;
  const truncate = t2 === undefined ? false : t2;
  const t3 = `Profile picture of ${formatUserFullName(review.firstName, review.lastName)}`;
  let t4;
  if ($[0] !== review.userImageUrl || $[1] !== t3) {
    t4 = <AvatarImage src={review.userImageUrl} alt={t3} />;
    $[0] = review.userImageUrl;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== review.firstName[0] || $[4] !== review.lastName[0]) {
    t5 = formatUserFullName(review.firstName[0], review.lastName[0]);
    $[3] = review.firstName[0];
    $[4] = review.lastName[0];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = <AvatarFallback>{t5}</AvatarFallback>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t4 || $[9] !== t6) {
    t7 = <Avatar>{t4}{t6}</Avatar>;
    $[8] = t4;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== review.firstName || $[12] !== review.lastName[0]) {
    t8 = formatUserFullName(review.firstName, review.lastName[0]);
    $[11] = review.firstName;
    $[12] = review.lastName[0];
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <ReviewTitle>{t8}</ReviewTitle>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== review.reviewedAt) {
    t10 = formatDistanceToNowStrict(new Date(review.reviewedAt), {
      addSuffix: true
    });
    $[16] = review.reviewedAt;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10) {
    t11 = <ReviewSubtitle><p className="line-clamp-1 text-xs">{t10}</p></ReviewSubtitle>;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t9) {
    t12 = <div className="flex flex-col gap-1">{t9}{t11}</div>;
    $[20] = t11;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t7) {
    t13 = <ReviewHeader>{t7}{t12}</ReviewHeader>;
    $[23] = t12;
    $[24] = t7;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const t14 = truncate ? "line-clamp-4" : "";
  let t15;
  if ($[26] !== review.reviewText || $[27] !== t14) {
    t15 = <ReviewBody><p className={t14}>{review.reviewText}</p></ReviewBody>;
    $[26] = review.reviewText;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== review.rating || $[30] !== showRating) {
    t16 = showRating ? <RatingStars count={review.rating} colorVariant="orange" starSize={16} /> : null;
    $[29] = review.rating;
    $[30] = showRating;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t16) {
    t17 = <ReviewFooter>{t16}</ReviewFooter>;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== children || $[35] !== className || $[36] !== t13 || $[37] !== t15 || $[38] !== t17) {
    t18 = <ReviewContainer className={className}>{t13}{t15}{t17}{children}</ReviewContainer>;
    $[34] = children;
    $[35] = className;
    $[36] = t13;
    $[37] = t15;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  return t18;
}
export function ReviewCardLikeMagicUI(t0) {
  const $ = _c(30);
  const {
    review,
    showRating: t1,
    className,
    children
  } = t0;
  const showRating = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== className) {
    t2 = cn("relative min-h-40 w-60 cursor-pointer overflow-hidden rounded-xl border bg-card p-4 transition hover:brightness-90", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== review.firstName || $[3] !== review.lastName || $[4] !== review.userImageUrl) {
    t3 = review.userImageUrl ? <Image src={review.userImageUrl} alt={`Profile picture of ${formatUserFullName(review.firstName, review.lastName)}`} width="32" height="32" className="rounded-full" /> : null;
    $[2] = review.firstName;
    $[3] = review.lastName;
    $[4] = review.userImageUrl;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== review.firstName || $[7] !== review.lastName) {
    t4 = formatUserFullName(review.firstName, review.lastName);
    $[6] = review.firstName;
    $[7] = review.lastName;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <figcaption className="text-sm font-medium dark:text-white">{t4}</figcaption>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== review.username) {
    t6 = <p className="text-xs font-medium dark:text-white/40">{review.username}</p>;
    $[11] = review.username;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <div className="flex flex-col">{t5}{t6}</div>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t3 || $[17] !== t7) {
    t8 = <div className="flex flex-row items-center gap-2">{t3}{t7}</div>;
    $[16] = t3;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== review.reviewText) {
    t9 = <blockquote className="mt-2 line-clamp-3 text-sm">{review.reviewText}</blockquote>;
    $[19] = review.reviewText;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== review.rating || $[22] !== showRating) {
    t10 = showRating && <div className="mb-2 mt-4 flex items-center gap-1">{Array.from({
        length: 5
      }).map((_, i) => <Star key={i} size={16} className={cn("fill-muted stroke-muted dark:fill-muted dark:stroke-muted", i + 1 <= review.rating && "fill-reviewStar stroke-reviewStar dark:fill-reviewStar dark:stroke-reviewStar")} />)}</div>;
    $[21] = review.rating;
    $[22] = showRating;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== children || $[25] !== t10 || $[26] !== t2 || $[27] !== t8 || $[28] !== t9) {
    t11 = <figure className={t2}>{t8}{t9}{t10}{children}</figure>;
    $[24] = children;
    $[25] = t10;
    $[26] = t2;
    $[27] = t8;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}